import {
  useState, useMemo 
} from 'react';
import {
  Send, Clock, CheckCircle, AlertTriangle, DollarSign 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  FormModal,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
} from '@/components/ui/form-modal';
import {
  usePurchaseRequests,
  PurchaseRequestCard,
  PurchaseRequestDetailModal,
  type PurchaseRequest,
  type PurchaseRequestStatus,
  type PurchaseRequestPriority,
  PR_STATUS_LABELS,
} from '@/features/procurement';
import { toast } from 'sonner';

interface StatusTab {
  value: PurchaseRequestStatus | undefined;
  label: string;
}

const STATUS_TABS: StatusTab[] = [
  {
    value: undefined,
    label: 'All' 
  },
  {
    value: 'pending_approval',
    label: 'Pending' 
  },
  {
    value: 'approved',
    label: 'Approved' 
  },
  {
    value: 'sourcing',
    label: 'In Sourcing' 
  },
  {
    value: 'completed',
    label: 'Completed' 
  },
];

interface RequestFormData {
  title: string;
  priority: PurchaseRequestPriority;
  deliveryZone: string;
  requiredByDate: string;
  description: string;
}

export function RequestsView() {
  const [selectedStatus, setSelectedStatus] = useState<PurchaseRequestStatus | undefined>(undefined);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null);
  const [formData, setFormData] = useState<RequestFormData>({
    title: '',
    priority: 'normal',
    deliveryZone: '',
    requiredByDate: '',
    description: '',
  });

  const {
    data, isLoading, error, refetch 
  } = usePurchaseRequests({ status: selectedStatus });
  const { data: allData } = usePurchaseRequests();

  const requests = data?.data ?? [];

  const stats = useMemo(() => {
    const reqs = allData?.data ?? [];
    return {
      total: reqs.length,
      pending: reqs.filter((r) => r.status === 'pending_approval').length,
      approved: reqs.filter((r) => r.status === 'approved').length,
      totalValue: reqs.reduce((sum, r) => sum + r.estimatedTotal, 0),
    };
  }, [allData]);

  const handleAddRequest = () => {
    setFormData({
      title: '',
      priority: 'normal',
      deliveryZone: '',
      requiredByDate: '',
      description: '',
    });
    setShowAddModal(true);
  };

  const handleSaveRequest = () => {
    if (!formData.title || !formData.deliveryZone || !formData.requiredByDate) {
      toast.error('Please fill in all required fields');
      return;
    }
    toast.success('Purchase request created');
    setShowAddModal(false);
    refetch();
  };

  const handleApprove: (requestId: string) => void = () => {
    toast.success('Request approved');
    setSelectedRequest(null);
    refetch();
  };

  const handleReject: (requestId: string) => void = () => {
    toast.success('Request rejected');
    setSelectedRequest(null);
    refetch();
  };

  const handleCreateSourcingEvent: (requestId: string) => void = () => {
    toast.success('Sourcing event created from request');
    setSelectedRequest(null);
    refetch();
  };

  const handleBulkUpload = (file: File) => {
    toast.success(`Processing ${file.name}...`);
    setTimeout(() => {
      toast.success('Imported 3 purchase requests');
      refetch();
    }, 1500);
  };

  const handleCardClick = (request: PurchaseRequest) => {
    setSelectedRequest(request);
  };

  const exportData = requests.map((req) => ({
    requestNumber: req.requestNumber,
    title: req.title,
    status: PR_STATUS_LABELS[req.status],
    priority: req.priority,
    site: req.siteName,
    deliveryZone: req.deliveryZone,
    estimatedTotal: req.estimatedTotal,
    requiredByDate: req.requiredByDate,
    createdBy: req.createdByName,
    createdAt: req.createdAt,
  }));

  return (
    <>
      <CrudToolbar
        entityName="Purchase Request"
        entityNamePlural="Purchase Requests"
        selectedCount={0}
        onAdd={handleAddRequest}
        onBulkUpload={handleBulkUpload}
        exportData={exportData}
        exportFilename="purchase-requests.csv"
        canEdit={false}
        canDelete={false}
      />

      <FormModal
        title="Create Purchase Request"
        description="Create a new purchase request for approval"
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveRequest}
        saveLabel="Create Request"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="Title" required>
            <FormInput
              value={formData.title}
              onChange={(e) => setFormData({
                ...formData,
                title: e.target.value 
              })}
              placeholder="e.g., Weekly Seafood Order"
            />
          </FormField>
          <FormField label="Priority" required>
            <FormSelect
              value={formData.priority}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  priority: e.target.value as PurchaseRequestPriority 
                })
              }
              options={[
                {
                  value: 'low',
                  label: 'Low' 
                },
                {
                  value: 'normal',
                  label: 'Normal' 
                },
                {
                  value: 'high',
                  label: 'High' 
                },
                {
                  value: 'urgent',
                  label: 'Urgent' 
                },
              ]}
            />
          </FormField>
          <FormField label="Delivery Zone" required>
            <FormSelect
              value={formData.deliveryZone}
              onChange={(e) => setFormData({
                ...formData,
                deliveryZone: e.target.value 
              })}
              placeholder="Select zone..."
              options={[
                {
                  value: 'Port of Miami',
                  label: 'Port of Miami' 
                },
                {
                  value: 'Barcelona Port',
                  label: 'Barcelona Port' 
                },
                {
                  value: 'Southampton',
                  label: 'Southampton' 
                },
              ]}
            />
          </FormField>
          <FormField label="Required By Date" required>
            <FormInput
              type="date"
              value={formData.requiredByDate}
              onChange={(e) => setFormData({
                ...formData,
                requiredByDate: e.target.value 
              })}
            />
          </FormField>
        </div>
        <FormField label="Description">
          <FormTextarea
            value={formData.description}
            onChange={(e) => setFormData({
              ...formData,
              description: e.target.value 
            })}
            placeholder="Additional details about this request..."
            rows={2}
          />
        </FormField>
      </FormModal>

      {selectedRequest && (
        <PurchaseRequestDetailModal
          request={selectedRequest}
          isOpen={true}
          onClose={() => setSelectedRequest(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onCreateSourcingEvent={handleCreateSourcingEvent}
        />
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Requests
            </CardTitle>
            <Send className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Approval
            </CardTitle>
            <Clock className="h-5 w-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Approved</CardTitle>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Value
            </CardTitle>
            <DollarSign className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              ${stats.totalValue.toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <Button
            key={tab.label}
            variant={selectedStatus === tab.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedStatus(tab.value)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={`skel-${i.toString()}`} className="h-56 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="mb-4 h-12 w-12 text-red-500" />
            <p className="text-lg font-medium">Failed to load purchase requests</p>
            <Button variant="outline" className="mt-4" onClick={() => refetch()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && requests.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Send className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-lg font-medium">Purchase Requests</p>
            <p className="text-sm text-muted-foreground">
              Create and manage purchase requests before sourcing
            </p>
            <Button className="mt-4" onClick={handleAddRequest}>
              Create Request
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && requests.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {requests.map((request) => (
            <div
              key={request.requestId}
              className="cursor-pointer rounded-lg transition-all hover:ring-2 hover:ring-primary/50"
              onClick={() => handleCardClick(request)}
            >
              <PurchaseRequestCard
                request={request}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
